const toTrimmedString = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value).trim();
};

const normalizeIsoDate = (value) => {
  const raw = toTrimmedString(value);
  if (!raw) {
    return new Date(0).toISOString();
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return new Date(0).toISOString();
  }
  return parsed.toISOString();
};

const getFirstDefinedValue = (source, keys = []) => {
  if (!source || typeof source !== 'object') {
    return undefined;
  }

  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(source, key) && source[key] !== undefined && source[key] !== null) {
      return source[key];
    }
  }

  return undefined;
};

const normalizeBoolean = (value, fallbackValue = false) => {
  if (typeof value === 'boolean') {
    return value;
  }

  const raw = toTrimmedString(value).toLowerCase();
  if (!raw) {
    return fallbackValue;
  }

  if (['true', '1', 'yes'].includes(raw)) {
    return true;
  }

  if (['false', '0', 'no'].includes(raw)) {
    return false;
  }

  return fallbackValue;
};

const createEmptyHistoryMeta = () => ({
  subjectId: null,
  isLegacy: false,
  currentVisibleResultStatus: null,
  referenceCode: null
});

const extractHistoryMeta = (source = {}, relationMeta = null) => {
  const firstAnalysisRef = Array.isArray(source?.analysis_refs) ? source.analysis_refs[0] : null;
  const subjectIdRaw = getFirstDefinedValue(source, ['subject_id', 'subjectId']);
  const isLegacyRaw = getFirstDefinedValue(source, ['is_legacy', 'isLegacy']);
  const currentVisibleResultStatusRaw = getFirstDefinedValue(source, ['current_visible_result_status', 'currentVisibleResultStatus']);
  const referenceCodeRaw = getFirstDefinedValue(source, ['reference_code', 'referenceCode'])
    ?? getFirstDefinedValue(relationMeta, ['reference_code', 'referenceCode'])
    ?? getFirstDefinedValue(firstAnalysisRef, ['reference_code', 'referenceCode']);

  return {
    subjectId: toTrimmedString(subjectIdRaw) || null,
    isLegacy: normalizeBoolean(isLegacyRaw, false),
    currentVisibleResultStatus: toTrimmedString(currentVisibleResultStatusRaw) || null,
    referenceCode: toTrimmedString(referenceCodeRaw) || null,
    hasSubjectId: subjectIdRaw !== undefined && toTrimmedString(subjectIdRaw) !== '',
    hasIsLegacy: isLegacyRaw !== undefined && isLegacyRaw !== null && (typeof isLegacyRaw === 'boolean' || toTrimmedString(isLegacyRaw) !== ''),
    hasCurrentVisibleResultStatus: currentVisibleResultStatusRaw !== undefined && toTrimmedString(currentVisibleResultStatusRaw) !== '',
    hasReferenceCode: referenceCodeRaw !== undefined && toTrimmedString(referenceCodeRaw) !== ''
  };
};

const applyHistoryMeta = (target = {}, historyMeta = null) => {
  const nextMeta = {
    ...createEmptyHistoryMeta(),
    subjectId: target?.subjectId || null,
    isLegacy: typeof target?.isLegacy === 'boolean' ? target.isLegacy : false,
    currentVisibleResultStatus: target?.currentVisibleResultStatus || null,
    referenceCode: target?.referenceCode || null
  };

  if (!historyMeta) {
    return {
      ...target,
      ...nextMeta
    };
  }

  if (historyMeta.hasSubjectId) {
    nextMeta.subjectId = historyMeta.subjectId;
  }
  if (historyMeta.hasIsLegacy) {
    nextMeta.isLegacy = historyMeta.isLegacy;
  }
  if (historyMeta.hasCurrentVisibleResultStatus) {
    nextMeta.currentVisibleResultStatus = historyMeta.currentVisibleResultStatus;
  }
  if (historyMeta.hasReferenceCode) {
    nextMeta.referenceCode = historyMeta.referenceCode;
  }

  return {
    ...target,
    ...nextMeta
  };
};

const sortByUpdatedAtDesc = (left, right) => new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime();

const inferImageFileTypeFromUrl = (imageUrl) => {
  const raw = toTrimmedString(imageUrl);
  if (!raw) {
    return 'jpeg';
  }

  const normalizedUrl = raw.split('?')[0].split('#')[0];
  const extension = normalizedUrl.split('.').pop()?.toLowerCase() || '';
  if (!extension) {
    return 'jpeg';
  }

  return extension === 'jpg' ? 'jpeg' : extension;
};

const normalizeHistoryDetailItem = (item) => {
  const original = item?.original_image || {};
  const originalHashId = toTrimmedString(
    original.hash_id ||
    item?.original_hash_id ||
    item?.hash_id
  );
  const originalImageUrl = toTrimmedString(
    original.image_url ||
    item?.img_url ||
    item?.image_url
  );

  if (!originalHashId || !originalImageUrl) {
    return null;
  }

  const createdAt = normalizeIsoDate(item?.created_at || item?.createdAt);
  const updatedAt = normalizeIsoDate(item?.updated_at || item?.updatedAt || createdAt);
  const presetStyleId = toTrimmedString(
    item?.relation_meta?.preset_style_id ||
    item?.relation_meta?.presetStyleId ||
    item?.preset_style_id ||
    item?.presetStyleId
  );
  const referenceImageUrl = toTrimmedString(
    item?.analysis_refs?.[0]?.thumbnail_image_url ||
    item?.analysis_refs?.[0]?.thumbnailImageUrl ||
    item?.thumbnail_image_url ||
    item?.thumbnailImageUrl ||
    item?.relation_meta?.reference_image_url ||
    item?.relation_meta?.referenceImageUrl ||
    item?.reference_image_url ||
    item?.referenceImageUrl
  );
  const relationId = toTrimmedString(item?.user_image_relation_id || item?.relation_id);
  const groupId = toTrimmedString(
    item?.relation_group_id ||
    item?.group_id ||
    item?.groupId ||
    originalHashId
  ) || originalHashId;
  const historyMeta = extractHistoryMeta(item, item?.relation_meta);

  return {
    groupId,
    originalHashId,
    originalImageUrl,
    originalFileType: toTrimmedString(original.file_type || item?.file_type || 'jpeg') || 'jpeg',
    originalImageType: toTrimmedString(original.image_type || item?.image_type || 'original') || 'original',
    displayHashId: toTrimmedString(
      item?.processed_image?.hash_id ||
      item?.display_hash_id ||
      item?.displayHashId ||
      originalHashId
    ) || originalHashId,
    relationId: relationId || null,
    presetStyleId: presetStyleId || null,
    referenceImageUrl: referenceImageUrl || null,
    createdAt,
    updatedAt,
    userId: toTrimmedString(item?.user_id || item?.userId),
    subjectId: historyMeta.subjectId,
    isLegacy: historyMeta.isLegacy,
    currentVisibleResultStatus: historyMeta.currentVisibleResultStatus,
    referenceCode: historyMeta.referenceCode,
    historyMeta
  };
};

const createCanonicalGroup = (entry) => {
  const mainItem = applyHistoryMeta({
    displayHashId: entry.originalHashId,
    presetStyleId: null,
    relationId: null,
    imageUrl: entry.originalImageUrl,
    createdAt: entry.createdAt,
    updatedAt: entry.updatedAt
  }, entry?.presetStyleId ? null : entry.historyMeta);

  return applyHistoryMeta({
    groupId: entry.groupId,
    originalHashId: entry.originalHashId,
    primaryRelationId: entry.relationId,
    originalImageUrl: entry.originalImageUrl,
    originalFileType: entry.originalFileType,
    originalImageType: entry.originalImageType,
    createdAt: entry.createdAt,
    updatedAt: entry.updatedAt,
    items: [mainItem]
  }, entry?.presetStyleId ? null : entry.historyMeta);
};

const hasReferenceChild = (items, candidate) => items.some((item) => (
  item?.presetStyleId === candidate.presetStyleId &&
  item?.relationId === candidate.relationId &&
  item?.displayHashId === candidate.displayHashId
));

const normalizeHistorySummaryRef = (ref = {}, fallbackGroup = {}) => {
  const presetStyleId = toTrimmedString(
    ref?.preset_style_id ||
    ref?.presetStyleId ||
    ref?.style_id ||
    ref?.styleId ||
    ref?.reference_code ||
    ref?.referenceCode
  ) || null;
  const relationId = toTrimmedString(ref?.relation_id || ref?.user_image_relation_id || ref?.relationId) || null;
  const displayHashId = toTrimmedString(
    ref?.display_hash_id ||
    ref?.displayHashId ||
    ref?.hash_id ||
    ref?.hashId ||
    ref?.processed_image?.hash_id ||
    fallbackGroup?.originalHashId
  ) || (fallbackGroup?.originalHashId || null);
  const imageUrl = toTrimmedString(
    ref?.thumbnail_image_url ||
    ref?.thumbnailImageUrl ||
    ref?.reference_image_url ||
    ref?.referenceImageUrl ||
    ref?.image_url ||
    ref?.imageUrl ||
    ref?.thumbnail_url ||
    ref?.thumbnailUrl
  ) || null;
  const historyMeta = extractHistoryMeta(ref, ref?.relation_meta);
  const category = ref?.category && typeof ref.category === 'object' ? ref.category : null;

  return {
    presetStyleId,
    relationId,
    displayHashId,
    imageUrl,
    thumbnailImageUrl: imageUrl,
    category,
    raw: ref,
    createdAt: normalizeIsoDate(ref?.created_at || ref?.createdAt || fallbackGroup?.createdAt),
    updatedAt: normalizeIsoDate(ref?.updated_at || ref?.updatedAt || fallbackGroup?.updatedAt || fallbackGroup?.createdAt),
    historyMeta
  };
};

const parseHashFromGroupKey = (groupKey) => {
  const raw = toTrimmedString(groupKey);
  if (!raw || !raw.includes(':')) {
    return '';
  }
  const parts = raw.split(':');
  return parts[1] || '';
};

const normalizeHistorySummaryGroup = (group = {}) => {
  const groupKey = toTrimmedString(group?.group_key || group?.groupKey);
  const sourceHashId = toTrimmedString(group?.source?.source_hash_id || group?.source?.sourceHashId);
  const hashFromGroupKey = parseHashFromGroupKey(groupKey);

  const originalHashId = toTrimmedString(
    group?.original_hash_id ||
    group?.originalHashId ||
    sourceHashId ||
    hashFromGroupKey ||
    group?.hash_id ||
    group?.hashId
  );
  const groupId = toTrimmedString(
    group?.relation_group_id ||
    group?.group_id ||
    group?.groupId ||
    groupKey ||
    originalHashId
  ) || originalHashId;

  if (!groupId && !originalHashId) {
    return null;
  }

  const fallbackGroup = {
    groupId,
    originalHashId: originalHashId || groupId,
    createdAt: normalizeIsoDate(group?.created_at || group?.createdAt || group?.source?.created_at || group?.source?.createdAt),
    updatedAt: normalizeIsoDate(group?.updated_at || group?.updatedAt || group?.source?.updated_at || group?.source?.updatedAt || group?.created_at || group?.createdAt || group?.source?.created_at || group?.source?.createdAt)
  };

  const refs = Array.isArray(group?.refs)
    ? group.refs.map((ref) => normalizeHistorySummaryRef(ref, fallbackGroup)).filter(Boolean)
    : [];

  return {
    ...fallbackGroup,
    historyMeta: extractHistoryMeta(group, group?.relation_meta),
    refs,
    items: refs
  };
};

const normalizeHistorySummaryListItem = (item = {}) => {
  const originalHashId = toTrimmedString(
    item?.hash_id ||
    item?.original_hash_id ||
    item?.originalHashId ||
    item?.hashId
  );
  const originalImageUrl = toTrimmedString(
    item?.preview_image_url ||
    item?.previewImageUrl ||
    item?.original_image_url ||
    item?.originalImageUrl ||
    item?.img_url ||
    item?.image_url
  );

  if (!originalHashId || !originalImageUrl) {
    return null;
  }

  const createdAt = normalizeIsoDate(item?.created_at || item?.createdAt);
  const updatedAt = normalizeIsoDate(item?.updated_at || item?.updatedAt || createdAt);
  const presetStyleId = toTrimmedString(item?.preset_style_id || item?.presetStyleId) || null;

  return {
    groupId: toTrimmedString(
      item?.relation_group_id ||
      item?.group_id ||
      item?.groupId ||
      originalHashId
    ) || originalHashId,
    originalHashId,
    originalImageUrl,
    originalFileType: inferImageFileTypeFromUrl(originalImageUrl),
    createdAt,
    updatedAt,
    presetStyleId,
    relationId: toTrimmedString(item?.relation_id || item?.user_image_relation_id || item?.relationId) || null,
    historyMeta: extractHistoryMeta(item, item?.relation_meta)
  };
};

const findMatchingSummaryRef = (item, refs = []) => refs.find((ref) => {
  if (item?.presetStyleId && ref?.presetStyleId && item.presetStyleId === ref.presetStyleId) {
    return true;
  }

  if (item?.relationId && ref?.relationId && item.relationId === ref.relationId) {
    return true;
  }

  if (item?.displayHashId && ref?.displayHashId && item.displayHashId === ref.displayHashId) {
    return true;
  }

  return Boolean(item?.imageUrl && ref?.imageUrl && item.imageUrl === ref.imageUrl);
});

const deriveGroupHistoryMeta = (group = {}) => {
  const mainItem = group?.items?.[0] || createEmptyHistoryMeta();
  const referenceItems = Array.isArray(group?.items)
    ? group.items.filter((item) => item?.presetStyleId)
    : [];

  if (!referenceItems.length) {
    return {
      subjectId: mainItem.subjectId || null,
      isLegacy: typeof mainItem.isLegacy === 'boolean' ? mainItem.isLegacy : false,
      currentVisibleResultStatus: mainItem.currentVisibleResultStatus || null,
      referenceCode: mainItem.referenceCode || null
    };
  }

  if (referenceItems.length === 1) {
    const singleReference = referenceItems[0];
    return {
      subjectId: singleReference.subjectId || null,
      isLegacy: typeof singleReference.isLegacy === 'boolean' ? singleReference.isLegacy : false,
      currentVisibleResultStatus: singleReference.currentVisibleResultStatus || null,
      referenceCode: singleReference.referenceCode || null
    };
  }

  return createEmptyHistoryMeta();
};

const mergeCanonicalGroupsWithHistorySummary = (groups = [], rawHistoryGroups = []) => {
  const summaryGroups = Array.isArray(rawHistoryGroups)
    ? rawHistoryGroups.map((group) => normalizeHistorySummaryGroup(group)).filter(Boolean)
    : [];

  if (!summaryGroups.length) {
    return groups.map((group) => {
      const items = Array.isArray(group?.items)
        ? group.items.map((item) => applyHistoryMeta(item))
        : [];
      const derivedGroupMeta = deriveGroupHistoryMeta({ ...group, items });
      return {
        ...applyHistoryMeta(group, null),
        ...derivedGroupMeta,
        items
      };
    });
  }

  const summaryMap = new Map();
  summaryGroups.forEach((group) => {
    const key = group.groupId || group.originalHashId;
    if (key) {
      summaryMap.set(key, group);
    }
    // 同时以 originalHashId 索引，确保不同 groupId 格式下仍能匹配
    if (group.originalHashId && group.originalHashId !== key) {
      summaryMap.set(group.originalHashId, group);
    }
  });

  return groups.map((group) => {
    const summaryGroup = summaryMap.get(group?.groupId || group?.originalHashId)
      || summaryMap.get(group?.originalHashId)
      || summaryMap.get(group?.groupId)
      || null;

    const items = Array.isArray(group?.items)
      ? group.items.map((item, index) => {
        if (index === 0) {
          return applyHistoryMeta(item, summaryGroup?.historyMeta || null);
        }

        const summaryRef = findMatchingSummaryRef(item, summaryGroup?.refs || []);
        const itemWithSummaryRef = summaryRef
          ? {
              ...item,
              imageUrl: summaryRef.imageUrl || item.imageUrl,
              thumbnailImageUrl: summaryRef.thumbnailImageUrl,
              category: summaryRef.category,
              raw: summaryRef.raw
            }
          : item;
        return applyHistoryMeta(itemWithSummaryRef, summaryRef?.historyMeta || null);
      })
      : [];

    // 将 history_groups 中存在但 detail 路径未产出的 refs 补充为 reference items
    if (summaryGroup) {
      const existingRefKeys = new Set(
        items
          .filter((i) => i?.presetStyleId)
          .map((i) => `${i.presetStyleId || ''}::${i.relationId || ''}::${i.displayHashId || ''}`)
      );

      (summaryGroup.refs || []).forEach((ref) => {
        const refKey = `${ref.presetStyleId || ''}::${ref.relationId || ''}::${ref.displayHashId || ''}`;
        if (existingRefKeys.has(refKey)) {
          return;
        }
        existingRefKeys.add(refKey);

        const newRefItem = applyHistoryMeta({
          displayHashId: ref.displayHashId,
          presetStyleId: ref.presetStyleId,
          relationId: ref.relationId,
          imageUrl: ref.imageUrl,
          thumbnailImageUrl: ref.thumbnailImageUrl,
          category: ref.category,
          raw: ref.raw,
          createdAt: ref.createdAt,
          updatedAt: ref.updatedAt
        }, ref.historyMeta);

        items.push(newRefItem);
      });
    }

    const derivedGroupMeta = summaryGroup?.historyMeta
      ? applyHistoryMeta(deriveGroupHistoryMeta({ ...group, items }), summaryGroup.historyMeta)
      : deriveGroupHistoryMeta({ ...group, items });

    return {
      ...applyHistoryMeta(group, summaryGroup?.historyMeta || null),
      ...derivedGroupMeta,
      items: [
        items[0],
        ...items.slice(1).sort(sortByUpdatedAtDesc)
      ]
    };
  });
};

export const HISTORY_LEGACY_FALLBACK_CLEANUP_MARKER = 'T-DAT-01-remove-history-store-legacy-fallback-after-T-HIS-02';

export function mapHistoryDetailToCanonicalGroups(detailList = []) {
  if (!Array.isArray(detailList)) {
    throw new TypeError('history detail list must be an array');
  }

  const groups = new Map();

  detailList.forEach((item) => {
    const entry = normalizeHistoryDetailItem(item);
    if (!entry) {
      return;
    }

    const groupKey = entry.groupId || entry.originalHashId;
    const group = groups.get(groupKey) || createCanonicalGroup(entry);

    if (!groups.has(groupKey)) {
      groups.set(groupKey, group);
    }

    if (!group.primaryRelationId && entry.relationId) {
      group.primaryRelationId = entry.relationId;
    }

    if (new Date(entry.createdAt).getTime() < new Date(group.createdAt).getTime()) {
      group.createdAt = entry.createdAt;
      group.items[0].createdAt = entry.createdAt;
    }

    if (new Date(entry.updatedAt).getTime() > new Date(group.updatedAt).getTime()) {
      group.updatedAt = entry.updatedAt;
      group.items[0].updatedAt = entry.updatedAt;
    }

    if (!entry.presetStyleId) {
      group.items[0] = applyHistoryMeta(group.items[0], entry.historyMeta);
      const groupWithMeta = applyHistoryMeta(group, entry.historyMeta);
      group.subjectId = groupWithMeta.subjectId;
      group.isLegacy = groupWithMeta.isLegacy;
      group.currentVisibleResultStatus = groupWithMeta.currentVisibleResultStatus;
      group.referenceCode = groupWithMeta.referenceCode;
      return;
    }

    const referenceItem = applyHistoryMeta({
      displayHashId: entry.displayHashId || entry.originalHashId,
      presetStyleId: entry.presetStyleId,
      relationId: entry.relationId,
      imageUrl: entry.referenceImageUrl || entry.originalImageUrl,
      thumbnailImageUrl: entry.referenceImageUrl || null,
      createdAt: entry.createdAt,
      updatedAt: entry.updatedAt
    }, entry.historyMeta);

    if (!hasReferenceChild(group.items, referenceItem)) {
      group.items.push(referenceItem);
    }
  });

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      ...deriveGroupHistoryMeta(group),
      items: [
        applyHistoryMeta(group.items[0]),
        ...group.items.slice(1).map((item) => applyHistoryMeta(item)).sort(sortByUpdatedAtDesc)
      ]
    }))
    .sort(sortByUpdatedAtDesc);
}

export function mapHistoryResponseToCanonicalGroups({ detailList = [], historyGroups = [] } = {}) {
  const canonicalGroups = mapHistoryDetailToCanonicalGroups(detailList);
  return mergeCanonicalGroupsWithHistorySummary(canonicalGroups, historyGroups);
}

export function mapHistorySummaryToCanonicalGroups(summaryList = []) {
  if (!Array.isArray(summaryList)) {
    return [];
  }

  const groups = new Map();

  summaryList.forEach((item) => {
    const entry = normalizeHistorySummaryListItem(item);
    if (!entry) {
      return;
    }

    const groupKey = entry.groupId || entry.originalHashId;
    const group = groups.get(groupKey) || applyHistoryMeta({
      groupId: entry.groupId,
      originalHashId: entry.originalHashId,
      primaryRelationId: entry.relationId,
      originalImageUrl: entry.originalImageUrl,
      originalFileType: entry.originalFileType,
      originalImageType: 'original',
      createdAt: entry.createdAt,
      updatedAt: entry.updatedAt,
      items: [applyHistoryMeta({
        displayHashId: entry.originalHashId,
        presetStyleId: null,
        relationId: null,
        imageUrl: entry.originalImageUrl,
        createdAt: entry.createdAt,
        updatedAt: entry.updatedAt
      }, null)]
    }, entry.historyMeta);

    if (!groups.has(groupKey)) {
      groups.set(groupKey, group);
    }

    if (!group.primaryRelationId && entry.relationId) {
      group.primaryRelationId = entry.relationId;
    }

    if (new Date(entry.createdAt).getTime() < new Date(group.createdAt).getTime()) {
      group.createdAt = entry.createdAt;
      group.items[0].createdAt = entry.createdAt;
    }

    if (new Date(entry.updatedAt).getTime() > new Date(group.updatedAt).getTime()) {
      group.updatedAt = entry.updatedAt;
      group.items[0].updatedAt = entry.updatedAt;
    }

    group.items[0] = applyHistoryMeta(group.items[0], entry.historyMeta);
    const groupWithMeta = applyHistoryMeta(group, entry.historyMeta);
    group.subjectId = groupWithMeta.subjectId;
    group.isLegacy = groupWithMeta.isLegacy;
    group.currentVisibleResultStatus = groupWithMeta.currentVisibleResultStatus;
    group.referenceCode = groupWithMeta.referenceCode;

    if (!entry.presetStyleId) {
      return;
    }

    const referenceItem = applyHistoryMeta({
      displayHashId: entry.originalHashId,
      presetStyleId: entry.presetStyleId,
      relationId: entry.relationId,
      imageUrl: entry.originalImageUrl,
      createdAt: entry.createdAt,
      updatedAt: entry.updatedAt
    }, entry.historyMeta);

    if (!hasReferenceChild(group.items, referenceItem)) {
      group.items.push(referenceItem);
    }
  });

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      ...deriveGroupHistoryMeta(group),
      items: [
        applyHistoryMeta(group.items[0]),
        ...group.items.slice(1).map((item) => applyHistoryMeta(item)).sort(sortByUpdatedAtDesc)
      ]
    }))
    .sort(sortByUpdatedAtDesc);
}

export function flattenHistoryGroupsToLegacyCards(groups = []) {
  if (!Array.isArray(groups)) {
    return [];
  }

  return groups
    .map((group) => {
      const originalHashId = toTrimmedString(group?.originalHashId || group?.items?.[0]?.displayHashId);
      const originalImageUrl = toTrimmedString(group?.originalImageUrl || group?.items?.[0]?.imageUrl);

      if (!originalHashId || !originalImageUrl) {
        return null;
      }

      const references = Array.isArray(group?.items)
        ? group.items.filter((item) => item?.presetStyleId)
        : [];

      return {
        id: originalHashId,
        url: originalImageUrl,
        name: `Image-${originalHashId}`,
        created_at: normalizeIsoDate(group?.createdAt),
        updated_at: normalizeIsoDate(group?.updatedAt || group?.createdAt),
        original_image: {
          hash_id: originalHashId,
          image_url: originalImageUrl,
          file_type: toTrimmedString(group?.originalFileType || 'jpeg') || 'jpeg',
          image_type: toTrimmedString(group?.originalImageType || 'original') || 'original'
        },
        user_image_relation_id: toTrimmedString(group?.primaryRelationId) || null,
        group_id: toTrimmedString(group?.groupId) || originalHashId,
        originalHashId,
        referenceCount: references.length,
        presetStyleId: null
      };
    })
    .filter(Boolean)
    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
}

export function mapHistoryDetailToLegacyItems(detailList = []) {
  if (!Array.isArray(detailList)) {
    return [];
  }

  return detailList
    .map((item) => {
      const normalized = normalizeHistoryDetailItem(item);
      if (!normalized) {
        return null;
      }

      return {
        id: normalized.originalHashId,
        url: normalized.originalImageUrl,
        name: `Image-${normalized.originalHashId}`,
        created_at: normalized.createdAt,
        updated_at: normalized.updatedAt,
        user_id: normalized.userId,
        original_image: {
          ...(item?.original_image || {}),
          hash_id: normalized.originalHashId,
          image_url: normalized.originalImageUrl,
          file_type: normalized.originalFileType,
          image_type: normalized.originalImageType
        },
        user_image_relation_id: normalized.relationId,
        presetStyleId: normalized.presetStyleId,
        relation_meta: {
          ...(item?.relation_meta || {}),
          preset_style_id: normalized.presetStyleId,
          reference_image_url: normalized.referenceImageUrl
        }
      };
    })
    .filter(Boolean)
    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
}
